// SelectCaixa.tsx
import { useEffect, useId, useMemo, useState } from "react";
import { verCaixas } from "services/api";
import type { Caixa } from "types/types";

export type SelectCaixaProps = {
    /** cod_identificacao atualmente selecionado */
    value: string;
    /** emite o cod_identificacao selecionado */
    onChange: (cod: string) => void;
    /** emite o OBJETO completo da caixa selecionada (ou null) */
    onSelectCaixa?: (c: Caixa | null) => void;

    id?: string;
    name?: string;
    label?: string;
    includeEmpty?: boolean;
    disabled?: boolean;
    className?: string;
    required?: boolean;
    /** se houver apenas 1 caixa e nada selecionado, seleciona automaticamente */
    autoSelectWhenSingle?: boolean;
    onUnauthorized?: () => void;
};

function fmtDim(v: string | number | undefined) {
    if (v == null || v === "") return "";
    const n = typeof v === "string" ? Number(v) : v;
    return Number.isFinite(n) ? String(n) : String(v);
}

export function SelectCaixa({
    value,
    onChange,
    onSelectCaixa,
    id,
    name = "caixa",
    label,
    includeEmpty = true,
    disabled = false,
    className = "cadastro-entrada",
    required = false,
    autoSelectWhenSingle = false,
    onUnauthorized,
}: SelectCaixaProps) {
    const reactId = useId();
    const selectId = id || `select-caixa-${reactId}`;

    const [caixas, setCaixas] = useState<Caixa[]>([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    // Carrega as caixas
    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setErro(null);
            try {
                const res = await verCaixas(); // GET /verCaixas
                const data = res?.data;
                if (!alive) return;

                if (Array.isArray(data)) {
                    setCaixas(data as Caixa[]);
                    // auto select quando há apenas 1 opção e nada selecionado
                    if (autoSelectWhenSingle && data.length === 1 && !value) {
                        const c = data[0] as Caixa;
                        onChange(c.cod_identificacao || "");
                        onSelectCaixa?.(c);
                    }
                } else {
                    setErro((data as any)?.erro || "Falha ao carregar caixas.");
                }
            } catch (e: any) {
                if (!alive) return;
                const status = e?.response?.status;
                if (status === 401 && onUnauthorized) onUnauthorized();
                setErro(e?.response?.data?.erro || e?.message || "Erro ao carregar caixas.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [reloadKey, autoSelectWhenSingle, onChange, onSelectCaixa, onUnauthorized, value]);

    // Ordena (código e desempate por id)
    const ordenadas = useMemo(() => {
        return [...caixas].sort((a, b) => {
            const aa = (a.cod_identificacao || "").localeCompare(
                b.cod_identificacao || "",
                undefined,
                { numeric: true, sensitivity: "base" }
            );
            if (aa !== 0) return aa;
            return (a.id as number) - (b.id as number);
        });
    }, [caixas]);

    // Sempre que o "value" mudar externamente, avisa o pai com o objeto correspondente
    useEffect(() => {
        const sel =
            ordenadas.find(c => (c.cod_identificacao || "") === (value || "")) || null;
        onSelectCaixa?.(sel);
    }, [value, ordenadas, onSelectCaixa]);
    return (
        <div className="cadastro-campo">
            <select
                id={selectId}
                name={name}
                className={className}
                value={value}
                onChange={(e) => {
                    const v = e.target.value;
                    onChange(v);
                    const cSel = ordenadas.find(c => (c.cod_identificacao || "") === v) || null;
                    onSelectCaixa?.(cSel);
                }}
                disabled={disabled || loading || !!erro}
                required={required}
                aria-busy={loading ? true : undefined}
                aria-invalid={!!erro || undefined}
            >
                {includeEmpty && <option value="">{loading ? "Carregando..." : "Selecione uma caixa"}</option>}
                {ordenadas.map((c) => {
                    const dims = `${fmtDim(c.altura)}x${fmtDim(c.largura)}x${fmtDim(c.comprimento)} cm`;
                    const label = c.descricao ? `${c.cod_identificacao} — ${c.descricao}` : `${c.cod_identificacao} — ${dims}`;
                    return (
                        <option key={c.id} value={c.cod_identificacao || ""}>
                            {label}
                        </option>
                    );
                })}
            </select>

            {loading && <small className="texto-aux">Carregando caixas…</small>}
            {erro && (
                <div className="texto-erro" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                    <small>{erro}</small>
                    <button
                        type="button"
                        className="botao botao--fantasma"
                        onClick={() => setReloadKey((k) => k + 1)}
                        style={{ padding: "2px 8px" }}
                    >
                        Tentar novamente
                    </button>
                </div>
            )}
        </div>
    );
}
